/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
/* eslint-disable max-lines-per-function */
/* eslint-disable prefer-arrow/prefer-arrow-functions */
/* eslint-disable complexity */
import { type I18nService, type LocalStorageService, type PermissionService } from '@dm/core';
import { type PanelMenuItem } from '@dm/core/types';
import { STORAGE_KEY } from 'app/common/constants';
import { LicenseKey } from 'app/common/constants/license-key';
import { type AuthService, type ToolService } from 'app/common/services';

import { FeatureConfig } from './feature';
import { ProductConfig, ShardSwitch, SWITCH } from './switch';

/**
 * 服务模块的左侧菜单
 * @param i18n
 */
export function serviceMenus(i18n: I18nService): PanelMenuItem[] {
    // dorado服务模块菜单
    const generalServiceLeftMenus: PanelMenuItem[] = [
        ...(SWITCH.IS_VSTORE_SYSTEM || !FeatureConfig.IsSupportVStoreService ? [] : [
            {
                label: i18n.get('service.vstore_term_vstoreService_title'),
                icon: 'app-icon-v-store',
                items: [
                    {
                        label: i18n.get('service.vstore_term_vstores_title'),
                        routerLink: ['/service/v-store-service/v-store'],
                        helpKey: 'vstoreManage',
                        show: [
                            {
                                permission: ['@vstore_R'],
                            },
                        ],
                    },
                ],
            },
        ]),
        {
            label: i18n.get('common.blockService_title'),
            icon: 'app-icon-lun',
            items: [
                {
                    label: i18n.get('common.lunGroup_title'),
                    routerLink: [`/service/block/${FeatureConfig.IsReplaceStrLunWithNamespace ? 'namespace-group' : 'lun-group'}`],
                    helpKey: 'lungroupView',
                    vstore: SWITCH.IS_VSTORE_SYSTEM || !FeatureConfig.IsSupportVStoreService ? null : 'all',
                    show: [
                        {
                            permission: [['lun_R', 'recycle_bin_R', 'lun_group_R']],
                            license: FeatureConfig.IsReplaceStrLunWithNamespace ? [] : [LicenseKey.SmartThin],
                        },
                    ],
                },
                {
                    label: i18n.get('common.hostGroups_title'),
                    routerLink: ['/service/block/hosts'],
                    helpKey: 'hostConfig',
                    vstore: SWITCH.IS_VSTORE_SYSTEM || !FeatureConfig.IsSupportVStoreService ? null : 'all',
                    show: [
                        {
                            permission: ['host_R'],
                            license: [LicenseKey.SmartThin],
                        },
                        {
                            permission: ['host_group_R'],
                            license: [LicenseKey.SmartThin],
                        },
                        {
                            permission: ['initiator_R'],
                            license: [LicenseKey.SmartThin],
                        },
                    ],
                },
                {
                    label: i18n.get('common.portGroups_title'),
                    routerLink: ['/service/block/port-group'],
                    helpKey: 'portgroupView',
                    vstore: SWITCH.IS_VSTORE_SYSTEM || !FeatureConfig.IsSupportVStoreService ? null : 'all',
                    show: [
                        {
                            permission: ['port_group_R'],
                            license: [LicenseKey.SmartThin],
                        },
                    ],
                },
                {
                    label: i18n.get('storage.vvol_task_vvol_tittle'),
                    routerLink: ['/service/block/vvol'],
                    helpKey: 'vvolManage',
                    vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'all',
                    show: [
                        {
                            permission: [['lun_R', 'protect_group_R']],
                            license: [LicenseKey.SmartThin],
                        },
                    ],
                    hide: !FeatureConfig.IsSupportVVOL,
                },
                {
                    label: i18n.get('storage.mappingView_task_title'),
                    routerLink: ['/service/block/mapping-views'],
                    helpKey: 'mappingView',
                    vstore: SWITCH.IS_VSTORE_SYSTEM || !FeatureConfig.IsSupportVStoreService ? null : 'all',
                    show: [
                        {
                            permission: ['mapping_view_R'],
                            license: [LicenseKey.SmartThin],
                        },
                    ],
                },
            ],
        },
        ...(SWITCH.SUPPORT_NAS ? [
            {
                label: i18n.get('common.fileService_title'),
                icon: 'app-icon-file-service',
                items: [
                    {
                        label: i18n.get('common.fileSystem_title'),
                        routerLink: ['/service/file/file-system'],
                        helpKey: 'fsManage',
                        vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'all',
                        show: [
                            {
                                permission: ['file_system_R'],
                            },
                        ],
                    },
                    {
                        label: i18n.get('common.dtree_lab'),
                        routerLink: ['/service/file/dtree'],
                        helpKey: 'dtreeManage',
                        vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'all',
                        show: [
                            {
                                permission: ['dtree_R'],
                            },
                        ],
                    },
                    {
                        label: i18n.get('common.share_title'),
                        routerLink: ['/service/file/share'],
                        helpKey: 'shareManage',
                        vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'except-all',
                        show: [
                            {
                                permission: ['share_R'],
                            },
                        ],
                    },
                    {
                        label: i18n.get('service.DataTurboClient_title'),
                        routerLink: ['/service/file/data-turbo-client'],
                        helpKey: 'dataTurboClientManage',
                        show: [
                            {
                                permission: ['data_turbo_R'],
                            },
                        ],
                        hide: SWITCH.IS_VSTORE_SYSTEM || !FeatureConfig.IsSupportDataTurboWhenNASIsSupported,
                    },
                    {
                        label: i18n.get('service.NFSClient_term_title'),
                        routerLink: ['/service/file/nfs-client'],
                        helpKey: 'nfsclientView',
                        vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'all',
                        show: [
                            {
                                permission: ['nfs_service_R'],
                            },
                        ],
                        hide: !FeatureConfig.IsSupportNFSClient,
                    },
                    {
                        label: i18n.get('common.accessUser_title'),
                        routerLink: ['/service/file/access-user'],
                        helpKey: 'accessUserManage',
                        vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'except-all',
                        show: [
                            {
                                permission: ['share_R'],
                            },
                        ],
                    },
                    {
                        label: i18n.get('common.quota_title'),
                        routerLink: ['/service/file/quota'],
                        helpKey: 'quotaManage',
                        vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'all',
                        show: [
                            {
                                permission: ['quota_R'],
                            },
                        ],
                    },
                ],
            },
        ] as PanelMenuItem[] : [
            {
                label: i18n.get('common.fileService_title'),
                icon: 'app-icon-file-service',
                items: [
                    {
                        label: i18n.get('common.EnumDefine_RUNNING_STATUS_E_UNACTIVATED'),
                        routerLink: ['/service/file/iniliziation'],
                        helpKey: 'fsActivate',
                        show: [
                            {
                                license: [LicenseKey.NAS],
                            },
                        ],
                    },
                ],
            },
        ] as PanelMenuItem[]),
        {
            label: i18n.get('system.network_tiitle'),
            icon: 'app-icon-network',
            items: [
                {
                    label: i18n.get('common.LogicalPorts'),
                    routerLink: ['/service/network/lif'],
                    helpKey: 'lifNetwork',
                    show: [
                        {
                            permission: ['port_R'],
                        },
                    ],
                    vstore: SWITCH.IS_VSTORE_SYSTEM || !FeatureConfig.IsSupportVStoreService ? null : 'all',
                },
                ...(SWITCH.IS_VSTORE_SYSTEM ? [] : [
                    {
                        label: i18n.get('system.network_eth_tiitle'),
                        routerLink: ['/service/network/eth'],
                        helpKey: 'ethernetnetwork',
                        show: [
                            {
                                permission: ['port_R'],
                            },
                        ],
                    },
                    {
                        label: i18n.get('system.network_roce_title'),
                        routerLink: ['/service/network/roce'],
                        helpKey: 'roceManage',
                        show: [
                            {
                                permission: ['port_R'],
                            },
                        ],
                    },
                ]),
                {
                    label: i18n.get('system.network_fc_tiitle'),
                    routerLink: ['/service/network/fc'],
                    helpKey: 'fcnetworkView',
                    show: [
                        {
                            permission: ['port_R'],
                        },
                    ],
                    hide: !FeatureConfig.IsSupportFC,
                },
                {
                    label: i18n.get('system.network_netSpaces_title'),
                    routerLink: ['/service/network/net-space'],
                    helpKey: 'spaceConfig',
                    show: [
                        {
                            permission: ['netspace_R'],
                        },
                    ],
                    hide: SWITCH.IS_VSTORE_SYSTEM || !SWITCH.SUPPORT_NET_SPACE,
                },
                // 交换机端口
                {
                    label: i18n.get('system.hardware_term_SOSwitchPorts_tab'),
                    routerLink: ['/service/network/switch-port'],
                    helpKey: 'switchportView',
                    show: [
                        {
                            permission: ['port_R'],
                        },
                    ],
                    hide: SWITCH.IS_VSTORE_SYSTEM || !SWITCH.SUPPORT_SHOW_SWITCH,
                },
            ],
        },
        {
            label: i18n.get('common.smartMigration_title'),
            icon: 'app-icon-resource-tuning',
            items: [
                {
                    label: i18n.get('storage.smartCache_term_title'),
                    routerLink: ['/service/tuning/smart-cache'],
                    helpKey: 'cachepoolConfigManage',
                    show: [
                        {
                            permission: ['smart_cache_R'],
                        },
                    ],
                    hide: SWITCH.IS_VSTORE_SYSTEM || !FeatureConfig.IsSupportSmartCache,
                },
                // 微存储直通模式不支持smartqos
                ...(SWITCH.IS_DIRECT_MODE ? [] as PanelMenuItem[] : [
                    {
                        label: i18n.get('monitor.charts_term_objectType_SmartQoS'),
                        routerLink: ['/service/tuning/smart-qos'],
                        helpKey: 'qosView',
                        show: [
                            {
                                permission: ['smart_qos_R'],
                                license: FeatureConfig.IsSupportSmartQosNoNeedLicense ? [] : [LicenseKey.SmartQoS],
                            },
                        ],
                        vstore: !SWITCH.IS_VSTORE_SYSTEM && FeatureConfig.IsSupportVStoreService ? 'all' : null,
                    },
                ] as PanelMenuItem[]),
                {
                    label: i18n.get('storage.smartMigration_menu'),
                    routerLink: ['/service/tuning/smart-migration'],
                    helpKey: 'migrationView',
                    vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'all',
                    show: [
                        {
                            permission: ['smart_migration_R'],
                            license: [LicenseKey.SmartMigration, LicenseKey.SmartThin],
                        },
                    ],
                    hide: !FeatureConfig.IsSupportSmartMigration,
                },
                {
                    label: i18n.get('storage.smartMove_menu'),
                    routerLink: ['/service/tuning/fs-smart-move'],
                    helpKey: 'moveConfig',
                    vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'all',
                    show: [
                        {
                            permission: [['smart_move_R',
                                'inter_smart_move_domain_R', 'inter_smart_move_vstore_R', 'inter_smart_move_fs_R']],
                            license: [LicenseKey.SmartMove],
                        },
                    ],
                    hide: !SWITCH.SUPPORT_SMART_MOVE,
                },
                ({
                    label: i18n.get('service.Cloudtier_task_title'),
                    routerLink: ['/service/tuning/smart-mobility'],
                    helpKey: 'mobilityConfig',
                    vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'all',
                    hide: !ShardSwitch.CLOUD_TIER,
                    show: [
                        {
                            permission: ['smart_mobility_R'],
                            license: [[LicenseKey.NAS], [LicenseKey.SmartMobilityNAS, LicenseKey.SmartMobilityObject]],
                        },
                    ],
                }),
            ],
        },
        ...(FeatureConfig.IsSupportContainerStorage ? [
            {
                label: i18n.get('storage.containerStorage_task_title'),
                icon: 'app-icon-container-storage',
                items: [
                    {
                        label: i18n.get('storage.containerStorage_task_pod_title'),
                        routerLink: ['/service/container-storage/pod'],
                        helpKey: 'podView',
                        hide: !FeatureConfig.IsSupportContainerStorage,
                        show: [
                            {
                                permission: ['container_storage_R'],
                            },
                        ],
                    },
                    {
                        label: i18n.get('storage.containerStorage_task_pv_title'),
                        routerLink: ['/service/container-storage/pv'],
                        helpKey: 'pvView',
                        hide: !FeatureConfig.IsSupportContainerStorage,
                        show: [
                            {
                                permission: ['container_storage_R'],
                            },
                        ],
                    },
                ],
            },
        ] : []),
    ];
    // 二级存储服务模块菜单
    const oceanProtectServiceLeftMenus: PanelMenuItem[] = [
        ...(SWITCH.SUPPORT_NAS ? [
            {
                label: i18n.get('common.fileService_title'),
                icon: 'app-icon-file-service',
                items: [
                    {
                        label: i18n.get('common.fileSystem_title'),
                        routerLink: ['/service/file/file-system'],
                        helpKey: 'fsManage',
                        vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'all',
                        show: [
                            {
                                permission: ['file_system_R'],
                            },
                        ],
                    },
                    {
                        label: i18n.get('common.dtree_lab'),
                        routerLink: ['/service/file/dtree'],
                        helpKey: 'dtreeManage',
                        vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'all',
                        show: [
                            {
                                permission: ['dtree_R'],
                            },
                        ],
                    },
                    {
                        label: i18n.get('common.share_title'),
                        routerLink: ['/service/file/share'],
                        helpKey: 'shareManage',
                        vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'except-all',
                        show: [
                            {
                                permission: ['share_R'],
                            },
                        ],
                    },
                    {
                        label: i18n.get('service.DataTurboClient_title'),
                        routerLink: ['/service/file/data-turbo-client'],
                        helpKey: 'dataTurboClientManage',
                        show: [
                            {
                                permission: ['data_turbo_R'],
                            },
                        ],
                        hide: SWITCH.IS_VSTORE_SYSTEM,
                    },
                    {
                        label: i18n.get('service.NFSClient_term_title'),
                        routerLink: ['/service/file/nfs-client'],
                        helpKey: 'nfsclientView',
                        vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'all',
                        show: [
                            {
                                permission: ['nfs_service_R'],
                            },
                        ],
                        hide: !FeatureConfig.IsSupportNFSClient,
                    },
                    {
                        label: i18n.get('common.accessUser_title'),
                        routerLink: ['/service/file/access-user'],
                        helpKey: 'accessUserManage',
                        vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'except-all',
                        show: [
                            {
                                permission: ['share_R'],
                            },
                        ],
                    },
                    {
                        label: i18n.get('common.quota_title'),
                        routerLink: ['/service/file/quota'],
                        helpKey: 'quotaManage',
                        vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'all',
                        show: [
                            {
                                permission: ['quota_R'],
                            },
                        ],
                    },
                ],
            },
        ] as PanelMenuItem[] : [
            {
                label: i18n.get('common.fileService_title'),
                icon: 'app-icon-file-service',
                items: [
                    {
                        label: i18n.get('common.EnumDefine_RUNNING_STATUS_E_UNACTIVATED'),
                        routerLink: ['/service/file/iniliziation'],
                        helpKey: 'fsActivate',
                        show: [
                            {
                                license: [LicenseKey.NAS],
                            },
                        ],
                    },
                ],
            },
        ] as PanelMenuItem[]),
        {
            label: i18n.get('system.network_tiitle'),
            icon: 'app-icon-network',
            items: [
                {
                    label: i18n.get('common.LogicalPorts'),
                    routerLink: ['/service/network/lif'],
                    helpKey: 'lifNetwork',
                    show: [
                        {
                            permission: ['port_R'],
                        },
                    ],
                    vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'all',
                },
                ...(SWITCH.IS_VSTORE_SYSTEM ? [] : [
                    {
                        label: i18n.get('system.network_eth_tiitle'),
                        routerLink: ['/service/network/eth'],
                        helpKey: 'ethernetnetwork',
                        show: [
                            {
                                permission: ['port_R'],
                            },
                        ],
                    },
                    ...(!FeatureConfig.IsSupportNVMEOverRoCENetWorkAndLif ? [] : [
                        {
                            label: i18n.get('system.network_roce_title'),
                            routerLink: ['/service/network/roce'],
                            helpKey: 'roceManage',
                            show: [
                                {
                                    permission: ['port_R'],
                                },
                            ],
                        },
                    ]),
                ]),
                {
                    label: i18n.get('system.network_fc_tiitle'),
                    routerLink: ['/service/network/fc'],
                    helpKey: 'fcnetworkView',
                    show: [
                        {
                            permission: ['port_R'],
                        },
                    ],
                },
                {
                    label: i18n.get('system.network_netSpaces_title'),
                    routerLink: ['/service/network/net-space'],
                    helpKey: 'spaceConfig',
                    show: [
                        {
                            permission: ['netspace_R'],
                        },
                    ],
                    hide: SWITCH.IS_VSTORE_SYSTEM || !SWITCH.SUPPORT_NET_SPACE,
                },
                // 交换机端口
                {
                    label: i18n.get('system.hardware_term_SOSwitchPorts_tab'),
                    routerLink: ['/service/network/switch-port'],
                    helpKey: '',
                    show: [
                        {
                            permission: ['port_R'],
                        },
                    ],
                    hide: SWITCH.IS_VSTORE_SYSTEM || !SWITCH.SUPPORT_SHOW_SWITCH,
                },
            ],
        },
        ...(SWITCH.IS_VSTORE_SYSTEM ? [] : [
            {
                label: i18n.get('service.vstore_term_vstoreService_title'),
                icon: 'app-icon-v-store',
                items: [
                    {
                        label: i18n.get('service.vstore_term_vstores_title'),
                        routerLink: ['/service/v-store-service/v-store'],
                        helpKey: 'vstoreManage',
                        show: [
                            {
                                permission: ['@vstore_R'],
                            },
                        ],
                    },
                ],
            },
        ]),
        {
            label: i18n.get('common.blockService_title'),
            icon: 'app-icon-lun',
            items: [
                {
                    label: i18n.get('common.lunGroup_title'),
                    routerLink: ['/service/block/lun-group'],
                    helpKey: 'lungroupView',
                    vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'all',
                    show: [
                        {
                            permission: [['lun_R', 'recycle_bin_R', 'lun_group_R']],
                            license: [LicenseKey.SmartThin],
                        },
                    ],
                },
                {
                    label: i18n.get('common.hostGroups_title'),
                    routerLink: ['/service/block/hosts'],
                    helpKey: 'hostConfig',
                    vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'all',
                    show: [
                        {
                            permission: ['host_R'],
                            license: [LicenseKey.SmartThin],
                        },
                        {
                            permission: ['host_group_R'],
                            license: [LicenseKey.SmartThin],
                        },
                        {
                            permission: ['initiator_R'],
                            license: [LicenseKey.SmartThin],
                        },
                    ],
                },
                {
                    label: i18n.get('common.portGroups_title'),
                    routerLink: ['/service/block/port-group'],
                    helpKey: 'portgroupView',
                    vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'all',
                    show: [
                        {
                            permission: ['port_group_R'],
                            license: [LicenseKey.SmartThin],
                        },
                    ],
                },
                {
                    label: i18n.get('storage.vvol_task_vvol_tittle'),
                    routerLink: ['/service/block/vvol'],
                    helpKey: 'vvolManage',
                    vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'all',
                    show: [
                        {
                            permission: [['lun_R', 'protect_group_R']],
                            license: [LicenseKey.SmartThin],
                        },
                    ],
                    hide: !FeatureConfig.IsSupportVVOL,
                },
                // 通道LUN
                {
                    label: i18n.get('storage.channelLUN_task_title'),
                    routerLink: ['/service/block/channel-lun'],
                    helpKey: 'channelLun',
                    vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'all',
                    hide: !FeatureConfig.IsSupportChannelLun,
                    show: [
                        {
                            permission: [['lun_R']],
                            license: [LicenseKey.SmartThin],
                        },
                    ],
                },
                {
                    label: i18n.get('storage.mappingView_task_title'),
                    routerLink: ['/service/block/mapping-views'],
                    helpKey: 'mappingView',
                    vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'all',
                    show: [
                        {
                            permission: ['mapping_view_R'],
                            license: [LicenseKey.SmartThin],
                        },
                    ],
                },
            ],
        },
        {
            label: i18n.get('common.smartMigration_title'),
            icon: 'app-icon-resource-tuning',
            items: [
                ...(SWITCH.IS_VSTORE_SYSTEM ? [] : [
                    {
                        label: i18n.get('storage.smartCache_term_title'),
                        routerLink: ['/service/tuning/smart-cache'],
                        helpKey: 'cachepoolConfigManage',
                        show: [
                            {
                                permission: ['smart_cache_R'],
                            },
                        ],
                        hide: SWITCH.IS_VSTORE_SYSTEM || !FeatureConfig.IsSupportSmartCache,
                    },
                ]),
                {
                    label: i18n.get('monitor.charts_term_objectType_SmartQoS'),
                    routerLink: ['/service/tuning/smart-qos'],
                    helpKey: 'qosView',
                    show: [
                        {
                            permission: ['smart_qos_R'],
                            license: FeatureConfig.IsSupportSmartQosNoNeedLicense ? [] : [LicenseKey.SmartQoS],
                        },
                    ],
                    vstore: !SWITCH.IS_VSTORE_SYSTEM ? 'all' : null,
                },
                {
                    label: i18n.get('storage.smartMigration_menu'),
                    routerLink: ['/service/tuning/smart-migration'],
                    helpKey: 'migrationView',
                    vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'all',
                    show: [
                        {
                            permission: ['smart_migration_R'],
                            license: [LicenseKey.SmartMigration, LicenseKey.SmartThin],
                        },
                    ],
                    hide: !FeatureConfig.IsSupportSmartMigration,
                },
                {
                    label: i18n.get('storage.smartMove_menu'),
                    routerLink: ['/service/tuning/fs-smart-move'],
                    helpKey: 'moveConfig',
                    vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'all',
                    show: [
                        {
                            permission: [['smart_move_R',
                                'inter_smart_move_domain_R', 'inter_smart_move_vstore_R', 'inter_smart_move_fs_R']],
                            license: [LicenseKey.SmartMove],
                        },
                    ],
                    hide: !SWITCH.SUPPORT_SMART_MOVE,
                },
                ({
                    label: i18n.get('service.Cloudtier_task_title'),
                    routerLink: ['/service/tuning/smart-mobility'],
                    helpKey: 'mobilityConfig',
                    vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'all',
                    hide: !ShardSwitch.CLOUD_TIER,
                    show: [
                        {
                            permission: ['smart_mobility_R'],
                            license: [[LicenseKey.NAS], [LicenseKey.SmartMobilityNAS, LicenseKey.SmartMobilityObject]],
                        },
                    ],
                }),
            ],
        },
    ];

    return !FeatureConfig.IsShowMenuItemsForOceanProtect ? generalServiceLeftMenus : oceanProtectServiceLeftMenus;
}

/**
 * 数据保护模块左侧菜单
 * @param i18n
 */
export function protectionMenus(i18n: I18nService, authService: AuthService, localStorageService: LocalStorageService): PanelMenuItem[] {
    // dorado数据保护左侧菜单
    const generalProtectLeftMenus: PanelMenuItem[] = [
        {
            label: i18n.get('protection.dataProtection_term_prtotectionEntities_label'),
            icon: 'app-icon-protect-object',
            items: [
                ...(SWITCH.SUPPORT_NAS && !SWITCH.IS_VSTORE_SYSTEM ? [
                    {
                        label: i18n.get('service.vstore_term_vstores_title'),
                        routerLink: ['/protection/v-store'],
                        helpKey: 'vstoreprotectionManage',
                        show: [
                            {
                                permission: ['hyper_metro_pair_R', '@vstore_R'],
                            },
                        ],
                    },
                ] : []),
                {
                    helpKey: 'pgView',
                    label: i18n.get('protection.protectionGroup_title'),
                    routerLink: ['/protection/object/protect-group'],
                    vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'all',
                    show: [
                        {
                            permission: ['protect_group_R'],
                            license: [LicenseKey.SmartThin],
                        },
                    ],
                },
                {
                    helpKey: 'lunprotectionView',
                    label: i18n.get('common.lun_title'),
                    routerLink: ['/protection/object/lun'],
                    vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'all',
                    show: [
                        {
                            permission: [['lun_R'], ['lun_snapshot_R', 'hyper_clone_R', 'remote_replication_R',
                                'hyper_metro_pair_R', 'dr_star_R']],
                            license: [LicenseKey.SmartThin],
                        },
                    ],
                },
                ...(SWITCH.SUPPORT_NAS ? [
                    {
                        helpKey: 'fsprotectionManage',
                        routerLink: ['/protection/object/filesystem'],
                        label: i18n.get('common.fileSystem_title'),
                        show: [
                            [
                                {
                                    permission: ['file_system_R'],
                                },
                            ], [
                                {
                                    permission: ['fs_snapshot_R'],
                                    license: ['HyperCDP'],
                                },
                                {
                                    permission: ['remote_replication_R'],
                                    license: ['HyperReplication'],
                                },
                                {
                                    license: ['HyperClone'],
                                },
                                {
                                    permission: ['hyper_metro_pair_R'],
                                },
                            ],
                        ],
                        vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'all',
                    },
                ] as PanelMenuItem[] : []),
            ],
        },
        {
            label: i18n.get('common.plan_title'),
            // icon: 'app-icon-plans',
            icon: 'app-icon-maintenance-record',
            items: [
                {
                    helpKey: 'hypercdpplanConfigManage',
                    label: i18n.get('protection.HyperCDP_term_block_title'),
                    routerLink: ['/protection/plan/hyper-cdp'],
                    show: [
                        {
                            permission: [['lun_R', 'protect_group_R'], ['@hyper_cdp_R']],
                            license: [LicenseKey.HyperCDP, LicenseKey.SmartThin],
                        },
                    ],
                    vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'all',
                },
                ...(SWITCH.SUPPORT_NAS ? [
                    {
                        helpKey: 'fshypercdpplanConfigManage',
                        label: i18n.get('protection.HyperCDP_term_fs_title'),
                        routerLink: ['/protection/plan/file-hyper-cdp'],
                        show: [
                            {
                                permission: ['file_system_R', '@hyper_cdp_R'],
                            },
                        ],
                        vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'all',
                    },
                ] as PanelMenuItem[] : []),
            ],
        },
        ...(SWITCH.IS_VSTORE_SYSTEM ? [] : [
            {
                label: i18n.get('common.configure_title'),
                icon: 'app-icon-configuration',
                items: [
                    {
                        helpKey: 'remotedeviceView',
                        label: i18n.get('common.remoteDevices_title'),
                        routerLink: ['/protection/config/remote-device'],
                        show: [
                            {
                                permission: ['remote_device_R'],
                            },
                        ],
                    },
                    {
                        helpKey: 'hyperdomainManage',
                        label: i18n.get('common.HyperMetroDomains_title'),
                        routerLink: ['/protection/config/hyper-metro-domain'],
                        show: [
                            {
                                permission: ['hyper_metro_domain_R'],
                                license: [[LicenseKey.NAS, LicenseKey.HyperMetro]],
                            },
                        ],
                    },
                    {
                        label: i18n.get('common.cloudBackup_title'),
                        externalLink: `https://${window.location.hostname}:25080/#/login`,
                        hide: authService.isRemoteAssistanceUser(),
                        show: [
                            {
                                permission: ['container_application_R'],
                                license: [LicenseKey.CloudBackup],
                            },
                        ],
                    },
                    {
                        label: i18n.get('common.HyperDetect_title'),
                        externalLink: `https://${window.location.hostname}:25080/#/login`,
                        hide: authService.isRemoteAssistanceUser() ||
                            !localStorageService.get(STORAGE_KEY.ANTIRANSOMWARE_APPLICATION_CHECK),
                        show: [
                            {
                                permission: ['container_application_R'],
                                license: [[LicenseKey.AntiRansomware, LicenseKey.ANTIRANSOMWARE_FOR_SAN]],
                            },
                        ],
                    },
                    {
                        label: i18n.get('common.HyperDetect_title'),
                        helpKey: 'deployAntiransomwareManage',
                        routerLink: ['/protection/config/deploy-antiransomware'],
                        hide: authService.isRemoteAssistanceUser() ||
                            localStorageService.get(STORAGE_KEY.ANTIRANSOMWARE_APPLICATION_CHECK),
                        show: [
                            {
                                permission: ['container_application_R'],
                                license: [[LicenseKey.AntiRansomware, LicenseKey.ANTIRANSOMWARE_FOR_SAN]],
                            },
                        ],
                    },
                ],
            },
        ]),
    ];
    // 二级存储数据保护左侧菜单
    const oceanProtectLeftMenus: PanelMenuItem[] = [
        {
            label: i18n.get('common.snapshot_title'),
            icon: 'app-icon-x-snapshot',
            helpKey: 'totalSnapshotView',
            routerLink: ['/protection/snapshot'],
            show: [
                {
                    permission: [['fs_snapshot_R', 'lun_snapshot_R', 'snap_group_R']],
                    license: [['HyperSnap', 'HyperClone', 'HyperCDP']],
                },
            ],
            vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'all',
        },
        {
            label: i18n.get('protection.fs_hyperClone_task_clones_title'),
            icon: 'app-icon-x-clone',
            helpKey: 'totalCloneView',
            routerLink: ['/protection/clone'],
            show: [
                {
                    permission: [['hyper_clone_group_R', 'hyper_clone_R', 'file_system_R']],
                    license: ['HyperClone'],
                },
            ],
            vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'all',
        },
        {
            label: i18n.get('common.replication_title'),
            icon: 'app-icon-x-replication',
            helpKey: 'totalRepView',
            routerLink: ['/protection/replication'],
            show: [
                {
                    permission: [['remote_replication_R', 'consistency_group_R']],
                    license: ['HyperReplication'],
                },
            ],
            vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'all',
        },
        {
            label: i18n.get('protection.protectionGroup_title'),
            icon: 'app-icon-x-protect-group',
            helpKey: 'xPgView',
            routerLink: ['/protection/protection-group'],
            show: [
                {
                    permission: ['protect_group_R'],
                    license: [LicenseKey.SmartThin],
                },
            ],
            vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'all',
        },
        ...(SWITCH.SUPPORT_NAS ? [
            {
                label: i18n.get('protection.CDP_term_fs_title'),
                icon: 'app-icon-protection-cdp-file',
                helpKey: 'fshypercdpplanConfigManage',
                routerLink: ['/protection/plan/file-hyper-cdp'],
                show: [
                    {
                        permission: ['file_system_R', '@hyper_cdp_R'],
                    },
                ],
                vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'all',
            },
        ] as PanelMenuItem[] : []),
        {
            helpKey: 'hypercdpplanConfigManage',
            icon: 'app-icon-protection-cdp-lun',
            label: i18n.get('protection.CDP_term_block_title'),
            routerLink: ['/protection/plan/hyper-cdp'],
            show: [
                {
                    permission: [['lun_R', 'protect_group_R'], ['@hyper_cdp_R']],
                    license: [LicenseKey.HyperCDP, LicenseKey.SmartThin],
                },
            ],
            vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'all',
        },
        ...(SWITCH.IS_VSTORE_SYSTEM ? [] : [
            {
                helpKey: 'remotedeviceView',
                icon: 'app-icon-x-remote-devices',
                label: i18n.get('common.remoteDevices_title'),
                routerLink: ['/protection/config/remote-device'],
                show: [
                    {
                        permission: ['remote_device_R'],
                    },
                ],
            },
            {
                icon: 'app-icon-x-hyper-detect',
                label: i18n.get('common.HyperDetect_title'),
                externalLink: `https://${window.location.hostname}:25080/#/login`,
                hide: authService.isRemoteAssistanceUser() ||
                    !localStorageService.get(STORAGE_KEY.ANTIRANSOMWARE_APPLICATION_CHECK),
                show: [
                    {
                        permission: ['container_application_R'],
                        license: [[LicenseKey.AntiRansomware, LicenseKey.ANTIRANSOMWARE_FOR_SAN]],
                    },
                ],
            },
            {
                icon: 'app-icon-x-hyper-detect',
                label: i18n.get('common.HyperDetect_title'),
                helpKey: 'deployAntiransomwareManage',
                routerLink: ['/protection/config/deploy-antiransomware'],
                hide: authService.isRemoteAssistanceUser() ||
                    localStorageService.get(STORAGE_KEY.ANTIRANSOMWARE_APPLICATION_CHECK),
                show: [
                    {
                        permission: ['container_application_R'],
                        license: [[LicenseKey.AntiRansomware, LicenseKey.ANTIRANSOMWARE_FOR_SAN]],
                    },
                ],
            },
        ]),
    ];

    return !FeatureConfig.IsShowMenuItemsForOceanProtect ? generalProtectLeftMenus : oceanProtectLeftMenus;
}

/**
 * 监控模块左侧菜单
 * @param i18n
 */
export function monitorMenus(i18n: I18nService, authService: AuthService): PanelMenuItem[] {
    return [
        ...(SWITCH.IS_VSTORE_SYSTEM ? [] : [
            {
                label: i18n.get('common.alarmAndEvent_title'),
                routerLink: ['/monitor/alarms-and-events'],
                icon: 'app-icon-alarms',
                helpKey: 'alarmView',
                show: [
                    {
                        permission: ['alarm_R'],
                    },
                ],
            },
            {
                label: i18n.get('monitor.diagnosis_task_title'),
                routerLink: ['/monitor/fault-tree-diagnosis'],
                icon: 'app-icon-fault-tree-diagnosis',
                helpKey: 'diagnosisManage',
                show: [
                    {
                        permission: ['fault_diagnose_R'],
                    },
                ],
                hide: !SWITCH.SUPPORT_FAULT_TREE_DIAGNOSIS,
                vstore: FeatureConfig.IsSupportVStoreService ? ('all' as 'all' | 'except-all') : null,
                hideVstoreSelection: true,
            },
            {
                label: i18n.get('common.performance_title'),
                icon: 'app-icon-performance-analysis',
                items: [
                    // 微存储屏蔽监控面板
                    ...(FeatureConfig.IsHidePms ? [] : [
                        {
                            helpKey: 'dashboardView',
                            label: i18n.get('common.dashboard_title'),
                            routerLink: ['/monitor/performance/dashboard'],
                            show: [
                                [
                                    {
                                        permission: ['performance_R'],
                                    },
                                ],
                            ],
                        },
                    ]),
                    {
                        helpKey: 'dataView',
                        label: i18n.get('monitor.charts_term_chart_label'),
                        routerLink: ['/monitor/performance/analysis'],
                        show: [
                            {
                                permission: ['performance_R'],
                            },
                        ],
                    },
                    // arm环境才支持异常检测
                    ...(SWITCH.SUPPORT_ABNORMAL_DETECTION && SWITCH.PRODUCT_SUPPORT_AI ? [
                        {
                            helpKey: 'exceptionDetection',
                            label: i18n.get('monitor.anomalyDetect_task_title'),
                            routerLink: ['/monitor/performance/performance-anomaly'],
                            show: [
                                {
                                    permission: ['performance_R'],
                                },
                            ],
                        },
                    ] : []),
                    {
                        helpKey: 'powerView',
                        label: i18n.get('common.powerConsumption_title'),
                        routerLink: ['/monitor/performance/power-consumption'],
                        hide: !FeatureConfig.IsSupportPowerView,  // 2910裁剪功耗
                        show: [
                            {
                                permission: ['power_supply_R'],
                            },
                        ],
                    },
                ],
            },
            // 2910和微存储屏蔽、产品不支持PMS AI相关特性，屏蔽预测
            ...(!(SWITCH.SUPPORT_CAPACITY_PREDICT) ? [] : [{
                helpKey: 'forecast',
                label: i18n.get('common.forecasting_newtitle'),
                routerLink: ['/monitor/forecasting'],
                icon: 'app-icon-forecast',
                show: [
                    {
                        permission: ['performance_R'],
                    },
                ],
            }]),
            {
                helpKey: 'reporttask',
                label: i18n.get('monitor.dataExport_title'),
                routerLink: ['/monitor/schedule-report'],
                icon: 'app-icon-report',
                hide: FeatureConfig.IsHidePms || authService.isRemoteAssistanceUser(),
                show: [
                    {
                        permission: ['performance_R'],
                    },
                ],
            },
        ]),
        {
            helpKey: 'taskcenter',
            label: i18n.get('common.taskCenter_title'),
            routerLink: ['/monitor/task'],
            icon: 'app-icon-task',
            vstore: SWITCH.IS_VSTORE_SYSTEM || !FeatureConfig.IsSupportVStoreService ? null : 'all',
            show: [
                {
                    permission: ['task_R'],
                },
            ],
        },
    ];
}

/**
 * 系统模块左侧菜单
 * @param i18n
 */
export function systemMenus(i18n: I18nService): PanelMenuItem[] {
    return [
        {
            label: i18n.get('common.hardware_title'),
            icon: 'app-icon-hardware',
            helpKey: 'deviceManage',
            routerLink: ['/system/hardware'],
            show: [
                {
                    permission: [
                        // 微存储不支持机柜&级联版【不需要该权限的依赖】
                        ...(FeatureConfig.IsHideCabinetAndExpansion ? [] : [
                            'cabinet_R',
                            'expansion_module_R',
                        ]),
                        'enclosure_R',
                        'controller_R',
                        'interface_module_R',
                        'fan_R',
                        'power_supply_R',
                        'bbu_R',
                        'disk_R',
                        'port_R',
                    ],
                },
            ],
        },
        // 微存储屏蔽外部存储
        ...(FeatureConfig.IsHideExternalStorage ? [] : [
            {
                label: i18n.get('common.externalStorage_title'),
                icon: 'app-icon-external-dev',
                helpKey: 'externalView',
                routerLink: ['/system/external-storage'],
                show: [
                    {
                        permission: ['remote_device_R'],
                        license: [LicenseKey.SmartThin],
                    },
                ],
            },
        ]),
        // 微存储直通模式屏蔽存储池
        ...(SWITCH.IS_DIRECT_MODE ? [] : [
            {
                label: i18n.get('common.storagePool_title'),
                icon: 'app-icon-storage-pool',
                helpKey: 'storagepoolView',
                routerLink: ['/system/storage-pool'],
                show: [
                    {
                        permission: ['storage_pool_R', 'disk_domain_R'],
                    },
                ],
            },
        ]),
        {
            label: i18n.get('system.container_task_title'),
            icon: 'app-icon-container-configuration',
            items: [
                // 应用
                ...getApplicationPanelMenuItems(i18n),
                // 虚拟机
                ...getVirtualMachinePanelMenuItems(i18n),
                // 镜像
                ...getRepositoryPanelMenuItems(i18n),
                // 网络平面
                {
                    helpKey: 'containernetworkManage',
                    label: i18n.get('system.container_networkPlane_task_title'),
                    routerLink: ['/system/smart-container/network-planes'],
                    show: [
                        {
                            permission: ['port_R'],
                            license: [[
                                LicenseKey.SmartContainerLicense,
                                LicenseKey.SmartContainerLicenseStandard,
                                LicenseKey.SmartContainerLicensePremium,
                                LicenseKey.CloudBackup,
                                LicenseKey.DataBackUpCapacity,
                                LicenseKey.AntiRansomware,
                                LicenseKey.ANTIRANSOMWARE_FOR_SAN,
                                LicenseKey.S3,
                                LicenseKey.VirtualEnvironmentEnabler,
                            ]],
                        },
                    ],
                },
            ],
        },
    ];
}

/**
 * 应用菜单
 * @param i18n
 */
export function getApplicationPanelMenuItems(i18n: I18nService): PanelMenuItem[] {
    return [
        {
            helpKey: 'containerApplicationManage',
            label: i18n.get('system.container_app_task_title'),
            routerLink: ['/system/smart-container/applications'],
            show: [
                {
                    permission: ['port_R', 'container_application_R'],
                    license: [[
                        LicenseKey.SmartContainerLicense,
                        LicenseKey.SmartContainerLicenseStandard,
                        LicenseKey.SmartContainerLicensePremium,
                        LicenseKey.CloudBackup,
                        LicenseKey.DataBackUpCapacity,
                        LicenseKey.AntiRansomware,
                        LicenseKey.ANTIRANSOMWARE_FOR_SAN,
                        LicenseKey.S3,
                    ]],
                },
            ],
            hide: !ShardSwitch.CONTAINER_REPOSITORY_AVAILABLE,
        },
    ];
}

/**
 * 容器仓库菜单
 * @param i18n
 */
export function getRepositoryPanelMenuItems(i18n: I18nService): PanelMenuItem[] {
    return [
        {
            helpKey: 'containerRepositorykManage',
            label: i18n.get('system.container_repository_task_title'),
            routerLink: ['/system/smart-container/repository'],
            show: [
                {
                    license: [[
                        LicenseKey.SmartContainerLicense,
                        LicenseKey.SmartContainerLicenseStandard,
                        LicenseKey.SmartContainerLicensePremium,
                        LicenseKey.CloudBackup,
                        LicenseKey.DataBackUpCapacity,
                        LicenseKey.AntiRansomware,
                        LicenseKey.ANTIRANSOMWARE_FOR_SAN,
                        LicenseKey.S3,
                        LicenseKey.VirtualEnvironmentEnabler,
                    ]],
                },
            ],
            hide: !ShardSwitch.CONTAINER_REPOSITORY_AVAILABLE,
        },
    ];
}

/**
 * 虚拟机菜单
 * @param i18n
 */
export function getVirtualMachinePanelMenuItems(i18n: I18nService): PanelMenuItem[] {
    return [
        {
            helpKey: 'vmManage',
            label: i18n.get('system.vm_app_task_title'),
            routerLink: ['/system/smart-container/virtual-machine'],
            show: [
                {
                    permission: ['vm_instance_R', 'port_R'],
                    license: [[
                        LicenseKey.VirtualEnvironmentEnabler,
                    ]],
                },
            ],
        },
    ];
}

/**
 * 设置模块左侧菜单
 * @param i18n
 */
export function settingsMenus(
    i18n: I18nService,
    toolService: ToolService,
    permissionService: PermissionService,
): PanelMenuItem[] {
    return [
        ...(SWITCH.IS_VSTORE_SYSTEM ? [] : [
            {
                label: i18n.get('common.basicInfo_title'),
                icon: 'app-icon-basic-infomation',
                items: [
                    {
                        helpKey: 'storagesysInfo',
                        label: i18n.get('common.device_info_label'),
                        routerLink: ['/settings/basic-infomation/device-info'],
                    },
                    {
                        helpKey: 'timeConfig',
                        label: i18n.get('common.systemTime_title'),
                        routerLink: ['/settings/basic-infomation/device-time'],
                    },
                    {
                        helpKey: 'dnsConfig',
                        label: i18n.get('common.dnsService_title'),
                        routerLink: ['/settings/basic-infomation/dns-service'],
                        show: [
                            {
                                permission: [['dns_server_R', 'dns_zone_R']],
                            },
                        ],
                    },
                    {
                        helpKey: 'storagesysUpgrade',
                        label: i18n.get('common.upgrade_label'),
                        routerLink: ['/settings/basic-infomation/upgrade'],
                        show: [
                            {
                                permission: ['support_major_W'],
                            },
                        ],
                        hide: ProductConfig.isHideUpgrade,
                    },
                    {
                        helpKey: 'emailConfig',
                        label: i18n.get('settings.EventNotification_mailService_title'),
                        routerLink: ['/settings/basic-infomation/email-service'],
                        show: [
                            {
                                permission: ['alarm_R'],
                            },
                        ],
                    },
                ],
            },
            {
                label: i18n.get('common.blockStorageService_title'),
                icon: 'app-icon-block-service-setting',
                helpKey: 'blockConfig',
                routerLink: ['/settings/block-storage-service'],
                show: [
                    {
                        permission: [['target_R', 'initiator_R', 'isns_R', 'host_R']],
                        license: [LicenseKey.SmartThin],
                    },
                ],
                hide: SWITCH.IS_DIRECT_MODE,
            },
        ]),
        ...(SWITCH.SUPPORT_NAS ? [
            {
                label: i18n.get('settings.fileStorageService_term_tittle'),
                icon: 'app-icon-file-service-setting',
                items: [
                    {
                        label: i18n.get('settings.fileStorageService_term_nfsService_tittle'),
                        routerLink: ['/settings/file-storage-service/nfs-service'],
                        helpKey: 'nfsService',
                        vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'except-all',
                        show: [
                            {
                                permission: ['nfs_service_R'],
                            },
                        ],
                    },
                    {
                        label: i18n.get('settings.fileStorageService_term_cifsService_title'),
                        routerLink: ['/settings/file-storage-service/cifs-service'],
                        helpKey: 'cifsService',
                        vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'except-all',
                        show: [
                            {
                                permission: ['cifs_service_R'],
                            },
                        ],
                    },
                    ...(SWITCH.IS_VSTORE_SYSTEM ? [
                        {
                            label: i18n.get('common.dnsService_title'),
                            routerLink: ['/settings/file-storage-service/vstore-dns-service'],
                            helpKey: 'dnsConfig',
                            show: [
                                {
                                    permission: ['domain_R'],
                                },
                            ],
                        },
                        {
                            label: i18n.get('system.logicPort_task_create_DNSZone_lab'),
                            routerLink: ['/settings/file-storage-service/vstore-dns-zone'],
                            helpKey: 'vstorednszone',
                            show: [
                                {
                                    permission: ['dns_zone_R'],
                                },
                            ],
                        },
                        {
                            label: i18n.get('settings.LDAPsetting_term_domainAuthen_title'),
                            routerLink: ['/settings/file-storage-service/vstore-domain'],
                            helpKey: 'ldapConfig',
                            show: [
                                {
                                    permission: [['ldap_R', 'domain_R']],
                                },
                            ],
                        },
                        {
                            label: i18n.get('service.vstore_task_kerberosDomain_title'),
                            routerLink: ['/settings/file-storage-service/vstore-kerberos'],
                            helpKey: 'kerberosManage',
                            show: [
                                {
                                    permission: ['kerberos_R'],
                                },
                            ],
                        },
                        {
                            label: i18n.get('settings.ndmp_task_title'),
                            routerLink: ['/settings/file-storage-service/vstore-ndmp'],
                            helpKey: 'ndmpSetting',
                            show: [
                                {
                                    permission: ['@ndmp_service_R'],
                                },
                            ],
                            hide: !ProductConfig.isSupportNDMP,
                        },
                    ] : [
                        {
                            label: i18n.get('settings.ndmp_task_title'),
                            helpKey: 'ndmpSetting',
                            routerLink: ['/settings/file-storage-service/file-ndmp-service'],
                            show: [
                                {
                                    permission: ['@ndmp_service_R'],
                                },
                            ],
                            hide: !ProductConfig.isSupportNDMP,
                        },
                    ]
                    ),
                    {
                        label: i18n.get('settings.auditLog_task_title'),
                        routerLink: ['/settings/file-storage-service/audit-log'],
                        helpKey: 'auditlogConfig',
                        vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'except-all',
                        show: [
                            {
                                permission: ['@audit_strategy_R'],
                            },
                        ],
                    },
                    {
                        label: i18n.get('settings.Antivirus_task_title'),
                        routerLink: ['/settings/file-storage-service/antivirus'],
                        helpKey: 'antivirusConfig',
                        vstore: SWITCH.IS_VSTORE_SYSTEM ? null : 'except-all',
                        show: [
                            {
                                permission: ['antivirus_R'],
                            },
                        ],
                        hide: !ProductConfig.isSupportAntivirus,
                    },
                ],
            },
        ] as PanelMenuItem[] : [
            {
                label: i18n.get('settings.fileStorageService_term_tittle'),
                icon: 'app-icon-file-service-setting',
                items: [
                    {
                        label: i18n.get('settings.fileStorageService_term_active_btn'),
                        helpKey: 'fsActivate',
                        routerLink: ['/settings/iniliziation'],
                        show: [
                            {
                                license: [LicenseKey.NAS],
                            },
                        ],
                    },
                ],
            },
        ] as PanelMenuItem[]),
        {
            label: i18n.get('settings.certificateManage_clientCertificates_col'),
            icon: 'app-icon-certificate-management',
            helpKey: 'certificateView',
            routerLink: ['/settings/certificate-management'],
            show: [
                {
                    permission: ['certificate_R'],
                },
            ],
            vstore: !SWITCH.IS_VSTORE_SYSTEM && FeatureConfig.IsSupportVStoreService ? 'all' : null,
        },
        {
            label: i18n.get('common.userAndSecurity_title'),
            icon: 'app-icon-user-security',
            items: [
                {
                    helpKey: 'userView',
                    label: i18n.get('common.userAndRole_title'),
                    routerLink: ['/settings/users-security/user-role'],
                },
                ...(SWITCH.IS_VSTORE_SYSTEM ? [] : [
                    {
                        helpKey: 'securityConfig',
                        label: i18n.get('common.securityPolicy_title_wintitle'),
                        routerLink: ['/settings/users-security/security-policy'],
                    },
                    {
                        helpKey: 'ldapConfig',
                        label: i18n.get('settings.LDAPsetting_term_domainAuthen_title'),
                        routerLink: ['/settings/users-security/domain-auth'],
                        show: [
                            {
                                permission: [['ldap_R', 'domain_R']],
                            },
                        ],
                        vstore: SWITCH.SUPPORT_NAS ? 'except-all' : null,
                    },
                    {
                        helpKey: 'multifactorauthentication',
                        label: i18n.get('settings.userAndSecurity_term_multifactorAuthen_label'),
                        routerLink: ['/settings/users-security/user-config'],
                        show: [
                            {
                                permission: ['user_R'],
                            },
                        ],
                    },
                    {
                        helpKey: 'ssoSetting',
                        label: i18n.get('settings.userAndSecurity_term_SSO_title'),
                        routerLink: ['/settings/users-security/sso-server'],
                        hide: !permissionService.isSuperAdmin(),
                    },
                ] as PanelMenuItem[]),
            ],
        },
        ...(SWITCH.IS_VSTORE_SYSTEM ? [] : [
            // 密钥服务
            {
                label: i18n.get('common.keyService_title'),
                icon: 'app-icon-key-service',
                helpKey: 'encryptView',
                routerLink: ['/settings/key-service'],
                show: [
                    {
                        permission: ['kmc_R'],
                    },
                ],
            },
            // 微存储直通场景屏蔽license
            ...(SWITCH.IS_DIRECT_MODE ? [] : [
                {
                    label: i18n.get('common.manageLicense_title'),
                    icon: 'app-icon-license',
                    helpKey: 'licenseView',
                    routerLink: ['/settings/license-management'],
                },
            ]),
            {
                label: i18n.get('common.setAlarm_title'),
                icon: 'app-icon-alarms',
                items: [
                    {
                        helpKey: 'alarmnotificationSet',
                        label: i18n.get('common.alarmNotification_title'),
                        routerLink: ['/settings/alarm/alarm-notify'], // Notification
                        show: [
                            {
                                permission: ['alarm_R'],
                            },
                        ],
                    },
                    {
                        helpKey: 'alarmSave',
                        label: i18n.get('common.dumpAlarm_title'),
                        routerLink: ['/settings/alarm/alarm-dump'],
                        show: [
                            {
                                permission: ['alarm_R'],
                            },
                        ],
                    },
                    {
                        helpKey: 'alarmMask',
                        label: i18n.get('common.maskAlarm_title'),
                        routerLink: ['/settings/alarm/alarm-mask'],
                        show: [
                            {
                                permission: ['alarm_R'],
                            },
                        ],
                    },
                    {
                        helpKey: 'eventnotificationSet',
                        label: i18n.get('settings.EventNotification_title'),
                        routerLink: ['/settings/alarm/alarm-event'],
                        show: [
                            {
                                permission: ['alarm_R'],
                            },
                        ],
                    },
                    {
                        helpKey: 'alarmLevelModify',
                        label: i18n.get('settings.alarmNotification_email_alarmLevel_label'),
                        routerLink: ['/settings/alarm/alarm-level'],
                        show: [
                            {
                                permission: ['alarm_R'],
                            },
                        ],
                    },
                ],
            },
            {
                label: i18n.get('common.snmpManagement_title'),
                icon: 'app-icon-snmp-manage',
                items: [
                    {
                        helpKey: 'snmpprotocol',
                        label: i18n.get('common.snmpProtocol_title'),
                        routerLink: ['/settings/snmp/snmp-protocol'],
                        show: [
                            {
                                permission: ['snmp_R'],
                            },
                        ],
                    },
                    {
                        helpKey: 'snmpsecuritypolicy',
                        label: i18n.get('common.snmpSecurityPolicy_title'),
                        routerLink: ['/settings/snmp/snmp-security-policy'],
                        show: [
                            {
                                permission: ['snmp_R'],
                            },
                        ],
                    },
                ],
            },
            {
                label: i18n.get('common.monitorSetting_title'),
                icon: 'app-icon-monitor-setting',
                helpKey: 'monitorSet',
                routerLink: ['/settings/monitor'],
                show: [
                    {
                        permission: ['performance_R'],
                    },
                ],
            },
            {
                label: i18n.get('settings.eService_title'),
                icon: 'app-icon-e-service',
                helpKey: 'eservice',
                routerLink: ['/settings/e-service'],
                show: [
                    {
                        permission: ['call_home_R'],
                    },
                ],
                hide: !FeatureConfig.IsSupportEService,
            },
            {
                label: i18n.get('settings.container_task_setting_title'),
                icon: 'app-icon-container-configuration',
                helpKey: 'containerSetting',
                routerLink: ['/settings/container-configuration'],
                show: [
                    {
                        permission: FeatureConfig.IsSupportVM ? ['vm_service_R'] : ['container_service_R'],
                        license: [[
                            LicenseKey.SmartContainerLicense,
                            LicenseKey.SmartContainerLicenseStandard,
                            LicenseKey.SmartContainerLicensePremium,
                            LicenseKey.CloudBackup,
                            LicenseKey.DataBackUpCapacity,
                            LicenseKey.AntiRansomware,
                            LicenseKey.ANTIRANSOMWARE_FOR_SAN,
                            LicenseKey.S3,
                            LicenseKey.VirtualEnvironmentEnabler,
                        ]],
                    },
                ],
            },
        ]),
    ];
}

/**
 * 租户模块左侧菜单
 * @param i18n
 */
export function vstoreMenus(i18n: I18nService, permission: PermissionService, vstoreName: string): PanelMenuItem[] {
    const supportSmartThin = permission.isLicenseExist([[LicenseKey.SmartThin]]);
    const haslunGroupPermission = permission.checkShow([['lun_R', 'recycle_bin_R', 'lun_group_R']], [LicenseKey.SmartThin]);
    const hashostsPermission = permission.checkShow([['host_R', 'host_group_R']], [LicenseKey.SmartThin]);
    const hasServiceFileSyestemPermission = SWITCH.SUPPORT_NAS && permission.checkShow(['file_system_R']);
    const hasServiceFileSyestemResult = hasServiceFileSyestemPermission ? '/service/file/file-system' : '';
    const hashostsResult = hashostsPermission ? '/service/block/hosts' : hasServiceFileSyestemResult;
    const lunGroupOrNamespaceGroup = `/service/block/${FeatureConfig.IsReplaceStrLunWithNamespace ? 'namespace-group' : 'lun-group'}`;
    const serviceURL = haslunGroupPermission ? lunGroupOrNamespaceGroup : hashostsResult;

    const hasProtectGroupPermission = permission.checkShow(['protect_group_R']);
    const hasProtectFileSyestemPermission = getProtectFsPermission(permission);

    // 二级存储数据保护目录与dorado不同，因此跳转路径也不同
    const pgUrl = FeatureConfig.IsShowMenuItemsForOceanProtect ? '/protection/protection-group' : '/protection/object/protect-group';
    const fsUrl = hasProtectFileSyestemPermission ? getFsProtectUrl(permission) : '';
    const protectionURL = hasProtectGroupPermission && supportSmartThin ? pgUrl : fsUrl;

    const hasDNSPermission = SWITCH.SUPPORT_NAS && permission.checkShow(['domain_R']);
    const hasCertificatePermission = permission.checkShow(['certificate_R']);
    const hasCertificateResult = hasCertificatePermission ? '/settings/certificate-management' : '';
    const settingURL = hasDNSPermission ? '/settings/file-storage-service/vstore-dns-service' : hasCertificateResult;

    return [
        {
            label: vstoreName,
            routerLink: ['/vstore/home'],
        },
        {
            label: i18n.get('common.service_title'),
            icon: 'app-icon-vstore-home-service',
            routerLink: [serviceURL],
            hide: !(haslunGroupPermission || hashostsPermission || hasServiceFileSyestemPermission),
        },
        {
            label: i18n.get('common.dataProtection_title'),
            icon: 'app-icon-protect-object',
            routerLink: [protectionURL],
            hide: !((hasProtectGroupPermission && supportSmartThin) || hasProtectFileSyestemPermission),
        },
        {
            label: i18n.get('common.setting_title'),
            icon: 'app-icon-configuration',
            routerLink: [settingURL],
            hide: !(hasDNSPermission || hasCertificatePermission),
        },
    ];
}

/**
 * 判断是否有数据保护-文件系统的权限
 */
export function getProtectFsPermission(permission: PermissionService): boolean {
    if (FeatureConfig.IsShowMenuItemsForOceanProtect) {
        // 判断是否有“克隆”、“快照”、“远程复制”的权限
        const hasFsOtherPermission = permission.checkShow(['file_system_R'], [LicenseKey.HyperClone]) ||
            permission.checkShow(['fs_snapshot_R'], [[LicenseKey.HyperSnap, LicenseKey.HyperClone, LicenseKey.HyperCDP]]) ||
            permission.checkShow(['remote_replication_R'], [LicenseKey.HyperReplication]);

        return SWITCH.SUPPORT_NAS && hasFsOtherPermission;
    } else {
        // 判断是否有“快照”、“远程复制pair”、“克隆”、“双活pair”的权限
        const hasFsOtherTabPermission = permission.checkShow(['fs_snapshot_R'], [LicenseKey.HyperCDP]) ||
            permission.checkShow(['remote_replication_R'], [LicenseKey.HyperReplication]) ||
            permission.isLicenseExist([LicenseKey.HyperClone]) ||
            permission.hasPermission('hyper_metro_pair_R');

        return SWITCH.SUPPORT_NAS && permission.hasPermission('file_system_R') && hasFsOtherTabPermission;
    }
}

/**
 * 跳转到数据保护-文件系统的url
 */
export function getFsProtectUrl(permission: PermissionService): string {
    if (FeatureConfig.IsShowMenuItemsForOceanProtect) {
        // 有“快照”权限则先跳转到快照
        if (permission.checkShow(['fs_snapshot_R'], [[LicenseKey.HyperSnap, LicenseKey.HyperClone, LicenseKey.HyperCDP]])) {
            return '/protection/snapshot';
        }

        // 其次判断“克隆”权限
        if (permission.checkShow(['file_system_R'], [LicenseKey.HyperClone])) {
            return '/protection/clone';
        }

        // 最后判断“远程复制”权限
        if (permission.checkShow(['remote_replication_R'], [LicenseKey.HyperReplication])) {
            return '/protection/replication';
        }

        return undefined as string;
    } else {
        return '/protection/object/filesystem';
    }
}


/**
 * 极简模式左侧菜单
 * @param i18n
 */
export function simpleModeMenus(i18n: I18nService): PanelMenuItem[] {
    return [
        // 首页
        {
            icon: 'app-icon-simple-home',
            label: i18n.get('common.mainPage_title'),
            routerLink: ['/simple/home'],
            vstore: null,
            hideContentHeader: true,
        },
        // LUN
        {
            icon: 'app-icon-simple-lun',
            label: i18n.get('common.lun_title'),
            routerLink: ['/simple/block/lun'],
            vstore: null,
            show: [
                {
                    permission: [['lun_R', 'lun_group_R']],
                    license: [LicenseKey.SmartThin],
                },
            ],
            helpKey: 'simplifyLUN',
        },
        // 主机
        {
            icon: 'app-icon-simple-host',
            label: i18n.get('common.hosts_title'),
            routerLink: ['/simple/block/hosts'],
            vstore: null,
            show: [
                {
                    permission: [['host_R', 'host_group_R']],
                    license: [LicenseKey.SmartThin],
                },
            ],
            helpKey: 'simplifyHost',
        },
        // 文件服务
        {
            icon: 'app-icon-simple-file-system',
            label: i18n.get('common.fileService_title'),
            routerLink: ['/simple/file-service'],
            vstore: null,
            show: [
                {
                    permission: [['file_system_R', 'share_R']],
                },
            ],
            hide: !SWITCH.SUPPORT_NAS,
            helpKey: 'simplifyFile',
        },
        // 数据保护
        {
            icon: 'app-icon-simple-protection',
            label: i18n.get('common.dataProtection_title'),
            routerLink: ['/simple/protection'],
            vstore: null,
            show: [
                {
                    // 有CDP License的情况下，有CDP权限即可
                    permission: ['@hyper_cdp_R'],
                    license: [LicenseKey.HyperCDP],
                },
                {
                    // 有LUN和LUN快照也可
                    permission: ['lun_R', 'lun_snapshot_R'],
                    license: [LicenseKey.SmartThin],
                },
                ...SWITCH.SUPPORT_NAS ?
                    [{
                        // 支持NAS，且有FS、FS快照权限也可
                        permission: ['file_system_R', 'fs_snapshot_R'],
                    }] : [],
            ],
            helpKey: 'simplifyProtection',
        },
        // 告警和事件
        {
            icon: 'app-icon-simple-alarm-event',
            label: i18n.get('common.alarmAndEvent_title'),
            routerLink: ['/simple/alarms-and-events'],
            vstore: null,
            show: [
                {
                    permission: ['alarm_R'],
                },
            ],
            helpKey: 'simplifyalarmManage',
        },
        // 性能监控
        {
            icon: 'app-icon-simple-performance-monitor',
            label: i18n.get('common.performance_title'),
            routerLink: ['/simple/performance-monitor'],
            vstore: null,
            show: [
                {
                    permission: ['performance_R'],
                },
            ],
            helpKey: 'dashboardView',
        },
        // 报表
        {
            icon: 'app-icon-simple-report',
            label: i18n.get('common.scheduleReport_title'),
            routerLink: ['/simple/monitor/schedule-report'],
            vstore: null,
            show: [
                {
                    permission: ['performance_R'],
                },
            ],
            helpKey: 'simplifyReport',
        },
        // 硬件
        {
            icon: 'app-icon-simple-hardware',
            label: i18n.get('monitor.storageHardware_simplifiedMode_title'),
            routerLink: ['/simple/system/hardware'],
            show: [
                {
                    permission: ['performance_R'],
                },
            ],
            helpKey: 'simplifyHardware',
        },
        // 设置
        {
            icon: 'app-icon-simple-settings',
            label: i18n.get('common.setting_title'),
            routerLink: ['/simple/settings'],
            show: [],
            helpKey: 'simplifySetting',
        },
    ];
}
